import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { environment } from '@environments/environment';
import { firstValueFrom } from 'rxjs';
import {
  MOCK_DEMO_CREDENTIALS,
  MOCK_REGISTER_DELAY_MS,
  MOCK_RESET_PASSWORD_DELAY_MS,
  MOCK_SERVER_ERROR_EMAIL,
} from './constants/auth-mock.constants';
import {
  AUTH_DEV_SESSION_STORAGE_KEY,
  AUTH_FIRST_LOGIN_STORAGE_KEY,
} from './constants/auth-session-storage.constants';
import { AuthErrorCode } from './enums/auth-error-code.enum';
import { IAuthClientSummary } from './interfaces/auth-client-summary.interface';
import { IAuthLoginResponse } from './interfaces/auth-login-response.interface';
import { IAuthMeResponse } from './interfaces/auth-me-response.interface';
import { IAuthSessionPayload } from './interfaces/auth-session-payload.interface';
import { IAuthUser } from './interfaces/auth-user.interface';
import { IClientSearchResult } from './interfaces/client-search-result.interface';
import { ILoginFormModel } from './interfaces/login-form-model.interface';
import { IRegisterPayload } from './interfaces/register-payload.interface';
import { LoginResult } from './types/login-result.type';
import { RegisterResult } from './types/register-result.type';
import { ResetPasswordResult } from './types/reset-password-result.type';

const PLATFORM_ADMIN_SCOPE = 'platform.admin';
export const SUPERADMIN_EMAIL = 'admin.sweet@sweetanalytics.com';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly document = inject(DOCUMENT);

  public readonly currentUser = signal<IAuthUser | null>(null);
  public readonly sessionToken = signal<string | null>(null);
  public readonly clientId = signal<string | null>(null);
  public readonly activeClientName = signal<string | null>(null);
  public readonly scopes = signal<readonly string[]>([]);
  public readonly canSwitchClient = signal(false);
  public readonly isFirstLogin = signal(this.readPersistedFirstLogin());
  public readonly isAuthenticated = computed(() => this.currentUser() !== null);
  public readonly isPlatformAdmin = computed(() => this.scopes().includes(PLATFORM_ADMIN_SCOPE));
  public readonly isSuperAdmin = computed(() => {
    const email = this.currentUser()?.email?.trim().toLowerCase();
    return email === SUPERADMIN_EMAIL;
  });
  public readonly activeClient = computed((): IAuthClientSummary | null => {
    const id = this.clientId();
    const name = this.activeClientName();
    if (!id || !name) {
      return null;
    }
    return { id, name, roleId: '' };
  });
  public readonly userInitials = computed(() => {
    const name = this.currentUser()?.name ?? '';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
      return '?';
    }
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
  });

  private isLoginInFlight = false;
  private isExchangeInFlight = false;
  private isRegisterInFlight = false;
  private isResetPasswordInFlight = false;
  private isSwitchInFlight = false;

  public async login(credentials: ILoginFormModel): Promise<LoginResult> {
    if (this.isLoginInFlight) {
      return { success: false, error: { code: AuthErrorCode.ServerError } };
    }

    this.isLoginInFlight = true;
    try {
      const response = await firstValueFrom(
        this.http.post<IAuthLoginResponse>(`${environment.apiUrl}/auth/login`, {
          email: credentials.email.trim().toLowerCase(),
          password: credentials.password,
        }),
      );
      const user = this.toAuthUser(response);
      this.establishSession(user, response);
      this.syncFirstLogin(response.isFirstLogin === true);
      return { success: true, user };
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        return { success: false, error: { code: AuthErrorCode.InvalidCredentials } };
      }
      return { success: false, error: { code: AuthErrorCode.ServerError } };
    } finally {
      this.isLoginInFlight = false;
    }
  }

  public async switchClient(clientId: string): Promise<boolean> {
    const token = this.sessionToken();
    if (!token || this.isSwitchInFlight) {
      return false;
    }

    this.isSwitchInFlight = true;
    try {
      const response = await firstValueFrom(
        this.http.post<IAuthLoginResponse>(
          `${environment.apiUrl}/auth/switch-client`,
          { clientId },
          { headers: { Authorization: `Bearer ${token}` } },
        ),
      );
      const user = this.toAuthUser(response);
      this.establishSession(user, response);
      this.reloadApp();
      return true;
    } catch {
      return false;
    } finally {
      this.isSwitchInFlight = false;
    }
  }

  public async exchangeGrant(code: string): Promise<boolean> {
    const trimmedCode = code.trim();
    if (!trimmedCode || this.isExchangeInFlight) {
      return false;
    }

    this.isExchangeInFlight = true;
    try {
      const response = await firstValueFrom(
        this.http.post<IAuthLoginResponse>(`${environment.apiUrl}/auth/exchange`, {
          code: trimmedCode,
        }),
      );
      const user = this.toAuthUser(response);
      this.establishSession(user, response);
      this.syncFirstLogin(response.isFirstLogin === true);
      return true;
    } catch {
      return false;
    } finally {
      this.isExchangeInFlight = false;
    }
  }

  public async searchClients(
    search: string,
    page: number,
    limit = 20,
  ): Promise<IClientSearchResult> {
    const token = this.sessionToken();
    if (!token) {
      return this.emptyClientSearchResult(page, limit);
    }

    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    const trimmedSearch = search.trim();
    if (trimmedSearch) {
      params.set('search', trimmedSearch);
    }

    try {
      return await firstValueFrom(
        this.http.get<IClientSearchResult>(`${environment.apiUrl}/clients/search?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      );
    } catch {
      return this.emptyClientSearchResult(page, limit);
    }
  }

  public async register(payload: IRegisterPayload): Promise<RegisterResult> {
    if (this.isRegisterInFlight) {
      return { success: false, error: { code: AuthErrorCode.ServerError } };
    }

    this.isRegisterInFlight = true;
    try {
      const result = await this.mockRegister(payload);
      if (result.success) {
        this.establishSession(result.user);
        this.syncFirstLogin(true);
      }
      return result;
    } finally {
      this.isRegisterInFlight = false;
    }
  }

  public async requestPasswordReset(email: string): Promise<ResetPasswordResult> {
    if (this.isResetPasswordInFlight) {
      return { success: false, error: { code: AuthErrorCode.ServerError } };
    }

    this.isResetPasswordInFlight = true;
    try {
      return await this.mockRequestPasswordReset(email);
    } finally {
      this.isResetPasswordInFlight = false;
    }
  }

  public establishSession(user: IAuthUser, response?: IAuthLoginResponse): void {
    this.currentUser.set(user);
    this.sessionToken.set(response?.token ?? null);
    this.clientId.set(response?.clientId ?? null);
    this.activeClientName.set(response?.activeClientName ?? null);
    this.scopes.set(response?.scopes ?? []);
    this.canSwitchClient.set(response?.canSwitchClient ?? false);
    this.persistSession({
      userId: user.id,
      email: user.email,
      name: user.name,
      token: response?.token,
      clientId: response?.clientId,
      activeClientName: response?.activeClientName,
      scopes: response?.scopes,
      canSwitchClient: response?.canSwitchClient,
    });
  }

  public consumeFirstLogin(): boolean {
    const isFirstLogin = this.isFirstLogin();
    if (isFirstLogin) {
      this.syncFirstLogin(false);
    }
    return isFirstLogin;
  }

  public logout(): void {
    const token = this.sessionToken();
    this.clearSessionState();

    if (!token) {
      return;
    }

    void firstValueFrom(
      this.http.post(
        `${environment.apiUrl}/auth/logout`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      ),
    ).catch(() => undefined);
  }

  public async restoreSession(): Promise<void> {
    const payload = this.readPersistedSession();
    if (!payload) {
      return;
    }

    const user = this.resolveUser(payload);
    if (!user) {
      this.clearSessionState();
      return;
    }

    if (!payload.token) {
      this.applyPersistedSession(user, payload);
      return;
    }

    try {
      const me = await firstValueFrom(
        this.http.get<IAuthMeResponse>(`${environment.apiUrl}/auth/me`, {
          headers: { Authorization: `Bearer ${payload.token}` },
        }),
      );
      this.applyValidatedSession(user, payload.token, me);
    } catch (error) {
      if (this.isInvalidSessionError(error)) {
        this.clearSessionState();
        return;
      }

      this.applyPersistedSession(user, payload);
    }
  }

  private clearSessionState(): void {
    this.currentUser.set(null);
    this.sessionToken.set(null);
    this.clientId.set(null);
    this.activeClientName.set(null);
    this.scopes.set([]);
    this.canSwitchClient.set(false);
    this.syncFirstLogin(false);
    this.clearPersistedSession();
  }

  private applyPersistedSession(user: IAuthUser, payload: IAuthSessionPayload): void {
    this.currentUser.set(user);
    this.sessionToken.set(payload.token ?? null);
    this.clientId.set(payload.clientId ?? null);
    this.activeClientName.set(payload.activeClientName ?? null);
    this.scopes.set(payload.scopes ?? []);
    this.canSwitchClient.set(payload.canSwitchClient ?? false);
  }

  private applyValidatedSession(user: IAuthUser, token: string, me: IAuthMeResponse): void {
    const validatedUser = this.toAuthUserFromMe(me.user, user);
    this.currentUser.set(validatedUser);
    this.sessionToken.set(token);
    this.clientId.set(me.clientId);
    this.activeClientName.set(me.activeClientName);
    this.scopes.set(me.scopes);
    this.canSwitchClient.set(me.canSwitchClient);
    this.persistSession({
      userId: validatedUser.id,
      email: validatedUser.email,
      name: validatedUser.name,
      token,
      clientId: me.clientId,
      activeClientName: me.activeClientName,
      scopes: me.scopes,
      canSwitchClient: me.canSwitchClient,
    });
  }

  private isInvalidSessionError(error: unknown): boolean {
    return error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403);
  }

  private toAuthUserFromMe(meUser: IAuthMeResponse['user'], fallback: IAuthUser): IAuthUser {
    return {
      id: meUser.id,
      email: meUser.email,
      name: meUser.name?.trim() || fallback.name,
    };
  }

  private reloadApp(): void {
    this.document.defaultView?.location.reload();
  }

  private toAuthUser(response: IAuthLoginResponse): IAuthUser {
    return {
      id: response.user.id,
      email: response.user.email,
      name: response.user.name?.trim() || response.user.email,
    };
  }

  private mockRegister(payload: IRegisterPayload): Promise<RegisterResult> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(this.evaluateMockRegistration(payload));
      }, MOCK_REGISTER_DELAY_MS);
    });
  }

  private mockRequestPasswordReset(email: string): Promise<ResetPasswordResult> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(
          email === MOCK_SERVER_ERROR_EMAIL
            ? { success: false, error: { code: AuthErrorCode.ServerError } }
            : { success: true },
        );
      }, MOCK_RESET_PASSWORD_DELAY_MS);
    });
  }

  private evaluateMockRegistration(payload: IRegisterPayload): RegisterResult {
    if (payload.email === MOCK_SERVER_ERROR_EMAIL) {
      return { success: false, error: { code: AuthErrorCode.ServerError } };
    }

    if (payload.email === MOCK_DEMO_CREDENTIALS.email) {
      return { success: false, error: { code: AuthErrorCode.EmailAlreadyExists } };
    }

    return {
      success: true,
      user: { id: `user-${Date.now()}`, email: payload.email, name: payload.name },
    };
  }

  private resolveUser(payload: IAuthSessionPayload): IAuthUser | null {
    if (payload.email && payload.name) {
      return { id: payload.userId, email: payload.email, name: payload.name };
    }

    return null;
  }

  private persistSession(payload: IAuthSessionPayload): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(AUTH_DEV_SESSION_STORAGE_KEY, JSON.stringify(payload));
  }

  private readPersistedSession(): IAuthSessionPayload | null {
    if (typeof localStorage === 'undefined') {
      return null;
    }

    const raw = localStorage.getItem(AUTH_DEV_SESSION_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    try {
      const parsed: unknown = JSON.parse(raw);
      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        'userId' in parsed &&
        typeof (parsed as IAuthSessionPayload).userId === 'string'
      ) {
        const payload = parsed as IAuthSessionPayload;
        return {
          userId: payload.userId,
          email: typeof payload.email === 'string' ? payload.email : undefined,
          name: typeof payload.name === 'string' ? payload.name : undefined,
          token: typeof payload.token === 'string' ? payload.token : undefined,
          clientId: typeof payload.clientId === 'string' ? payload.clientId : undefined,
          activeClientName:
            typeof payload.activeClientName === 'string' ? payload.activeClientName : undefined,
          scopes: Array.isArray(payload.scopes)
            ? payload.scopes.filter((scope): scope is string => typeof scope === 'string')
            : undefined,
          canSwitchClient:
            typeof payload.canSwitchClient === 'boolean' ? payload.canSwitchClient : undefined,
        };
      }
    } catch {
      return null;
    }

    return null;
  }

  private clearPersistedSession(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.removeItem(AUTH_DEV_SESSION_STORAGE_KEY);
  }

  private emptyClientSearchResult(page: number, limit: number): IClientSearchResult {
    return { clients: [], total: 0, page, limit, hasMore: false };
  }

  private syncFirstLogin(isFirstLogin: boolean): void {
    this.isFirstLogin.set(isFirstLogin);
    if (isFirstLogin) {
      this.persistFirstLoginFlag();
      return;
    }
    this.clearFirstLoginFlag();
  }

  private persistFirstLoginFlag(): void {
    const storage = this.document.defaultView?.sessionStorage;
    if (!storage) {
      return;
    }
    storage.setItem(AUTH_FIRST_LOGIN_STORAGE_KEY, '1');
  }

  private readPersistedFirstLogin(): boolean {
    const storage = this.document.defaultView?.sessionStorage;
    return storage?.getItem(AUTH_FIRST_LOGIN_STORAGE_KEY) === '1';
  }

  private clearFirstLoginFlag(): void {
    const storage = this.document.defaultView?.sessionStorage;
    storage?.removeItem(AUTH_FIRST_LOGIN_STORAGE_KEY);
  }
}
